import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { SetupChargeService } from "../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupCharge, ISetupChargeTotals } from "../../../shared/services/api/setupCharge/Types";
import { buildSetupChargeCsv, buildSetupChargeListParams, downloadSetupChargeCsv, EMPTY_SETUP_CHARGE_FILTERS, ISetupChargeFilterState, setupChargeFormatCurrency, setupChargeFormatDate } from "../utils/setupChargeFormat";
import { setupChargePaymentMethodLabel } from "../utils/setupChargeStatus";
import { downloadSetupChargeXlsx } from "../utils/setupChargeXlsx";
import { SetupChargeFilters } from "./SetupChargeFilters";
import { SetupChargeTotals } from "./SetupChargeTotals";

const PAGE_SIZE_EXPORT = 100;

/** O relatório é sempre das taxas pagas, pela data de pagamento (fechamento do ciclo). */
const REPORT_FILTERS: ISetupChargeFilterState = { ...EMPTY_SETUP_CHARGE_FILTERS, dateField: "paid", status: ["paid"] };

export const SetupChargeReport = () => {
	const [filters, setFilters] = useState<ISetupChargeFilterState>(REPORT_FILTERS);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [rows, setRows] = useState<ISetupCharge[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [totals, setTotals] = useState<ISetupChargeTotals | undefined>();
	const [isLoading, setIsLoading] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const load = async (current: ISetupChargeFilterState, currentPage: number, limit: number) => {
		setIsLoading(true);
		const result = await SetupChargeService.list(buildSetupChargeListParams(current, currentPage, limit));
		setIsLoading(false);
		if (result instanceof Error) {
			setRows([]);
			setTotalCount(0);
			setTotals(undefined);
			setErrorMessage(result.message);
			return;
		}
		setErrorMessage(null);
		setRows(result.data);
		setTotalCount(result.totalCount);
		setTotals(result.totals);
	};

	useEffect(() => {
		load(REPORT_FILTERS, 0, rowsPerPage);
	}, []);

	const handleApply = (next: ISetupChargeFilterState) => {
		const forced = { ...next, dateField: "paid" as const, status: ["paid"] };
		setFilters(forced);
		setPage(0);
		load(forced, 0, rowsPerPage);
	};

	const handleExport = async (format: "csv" | "xlsx") => {
		setIsExporting(true);
		setErrorMessage(null);
		const all: ISetupCharge[] = [];
		for (let current = 0; ; current += 1) {
			const result = await SetupChargeService.list(buildSetupChargeListParams(filters, current, PAGE_SIZE_EXPORT));
			if (result instanceof Error) {
				setErrorMessage(result.message);
				setIsExporting(false);
				return;
			}
			all.push(...result.data);
			if (all.length >= result.totalCount || result.data.length === 0) break;
		}
		const stamp = new Date().toISOString().slice(0, 10);
		const baseName = `taxa-setup-avulsa-${filters.dateStart || "inicio"}-a-${filters.dateEnd || stamp}`;
		if (format === "xlsx") await downloadSetupChargeXlsx(all, `${baseName}.xlsx`);
		else downloadSetupChargeCsv(buildSetupChargeCsv(all), `${baseName}.csv`);
		setIsExporting(false);
	};

	return (
		<Box>
			<SetupChargeFilters applied={filters} onApply={handleApply} compact disabled={isLoading || isExporting} />
			<Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mr={4}>
				<SetupChargeTotals totals={totals} />
				<Box display="flex" gap={1}>
					<Button variant="outlined" startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />} onClick={() => handleExport("xlsx")} disabled={isExporting || totalCount === 0}>
						Exportar planilha (XLSX)
					</Button>
					<Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={() => handleExport("csv")} disabled={isExporting || totalCount === 0}>
						CSV
					</Button>
				</Box>
			</Box>

			{errorMessage && (
				<Box mt={1} mx={1}>
					<Alert severity="error">{errorMessage}</Alert>
				</Box>
			)}

			<TableContainer component={Paper} sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "auto", maxHeight: "60vh" }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							{["Pagamento", "Imobiliária", "Pagador", "Valor", "Taxa", "Líquido", "Gateway", "Forma", "Repasse"].map((label, index) => (
								<TableCell key={label} align={index >= 3 && index <= 5 ? "right" : "left"} sx={{ backgroundColor: "grey.100" }}>
									<Typography variant="button">{label}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={9}>
									<Typography variant="body2" color="text.secondary" align="center">
										{isLoading ? "Carregando..." : "Nenhuma taxa paga no período."}
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{rows.map((row) => (
							<TableRow key={row.id} hover>
								<TableCell>{setupChargeFormatDate(row.paidAt)}</TableCell>
								<TableCell>{row.clientName ?? ""}</TableCell>
								<TableCell>{row.payerName ?? ""}</TableCell>
								<TableCell align="right">{setupChargeFormatCurrency(row.amount)}</TableCell>
								<TableCell align="right">{setupChargeFormatCurrency(row.gatewayFee)}</TableCell>
								<TableCell align="right">{setupChargeFormatCurrency(row.netAmount)}</TableCell>
								<TableCell>{row.gateway ?? ""}</TableCell>
								<TableCell>{setupChargePaymentMethodLabel(row.paymentMethod)}</TableCell>
								<TableCell>
									<Chip size="small" variant="outlined" color={row.repassed ? "success" : "warning"} label={row.repassed ? `Repassado ${setupChargeFormatCurrency(row.repassAmount)}` : `Pendente ${setupChargeFormatCurrency(row.expectedRepass)}`} />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, next) => {
						setPage(next);
						load(filters, next, rowsPerPage);
					}}
					onRowsPerPageChange={(e) => {
						const limit = parseInt(e.target.value, 10);
						setRowsPerPage(limit);
						setPage(0);
						load(filters, 0, limit);
					}}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</TableContainer>
		</Box>
	);
};
